import React, { useState, useEffect, useCallback } from "react";
import { getAuditLogs } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatDateTime } from "../lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  History,
  User,
  FileSpreadsheet,
  MessageSquare,
  Flag,
  Download,
  Users,
  RefreshCw,
} from "lucide-react";

const ACTION_TYPES = [
  { value: "all", label: "Toutes les actions" },
  { value: "CREATE_LEAD", label: "Création lead" },
  { value: "UPDATE_LEAD", label: "Mise à jour lead" },
  { value: "DELETE_LEAD", label: "Suppression lead" },
  { value: "CREATE_NOTE", label: "Création note" },
  { value: "UPDATE_NOTE", label: "Mise à jour note" },
  { value: "DELETE_NOTE", label: "Suppression note" },
  { value: "IMPORT_CSV", label: "Import CSV" },
  { value: "EXPORT_CSV", label: "Export CSV" },
  { value: "BULK_ASSIGN_LEADS", label: "Attribution en masse" },
  { value: "ROUND_ROBIN_ASSIGN", label: "Distribution auto" },
  { value: "CREATE_USER", label: "Création utilisateur" },
  { value: "UPDATE_USER", label: "Mise à jour utilisateur" },
  { value: "CREATE_STATUS", label: "Création statut" },
  { value: "UPDATE_STATUS", label: "Mise à jour statut" },
];

const ENTITIES = [
  { value: "all", label: "Toutes les entités" },
  { value: "Lead", label: "Leads" },
  { value: "LeadNote", label: "Notes" },
  { value: "User", label: "Utilisateurs" },
  { value: "Status", label: "Statuts" },
  { value: "ImportJob", label: "Imports" },
];

const getActionIcon = (actionType) => {
  if (actionType.includes("LEAD")) return <Users className="h-4 w-4" />;
  if (actionType.includes("NOTE")) return <MessageSquare className="h-4 w-4" />;
  if (actionType.includes("USER")) return <User className="h-4 w-4" />;
  if (actionType.includes("STATUS")) return <Flag className="h-4 w-4" />;
  if (actionType.includes("IMPORT") || actionType.includes("EXPORT"))
    return <FileSpreadsheet className="h-4 w-4" />;
  return <History className="h-4 w-4" />;
};

const getActionColor = (actionType) => {
  if (actionType.includes("CREATE")) return "bg-green-100 text-green-700";
  if (actionType.includes("UPDATE")) return "bg-blue-100 text-blue-700";
  if (actionType.includes("DELETE")) return "bg-red-100 text-red-700";
  if (actionType.includes("IMPORT") || actionType.includes("EXPORT"))
    return "bg-purple-100 text-purple-700";
  if (actionType.includes("ASSIGN")) return "bg-orange-100 text-orange-700";
  return "bg-gray-100 text-gray-700";
};

const getActionLabel = (actionType) => {
  const labels = {
    CREATE_LEAD: "Nouveau lead",
    UPDATE_LEAD: "Lead mis à jour",
    DELETE_LEAD: "Lead supprimé",
    CREATE_NOTE: "Note ajoutée",
    UPDATE_NOTE: "Note modifiée",
    DELETE_NOTE: "Note supprimée",
    IMPORT_CSV: "Import CSV",
    EXPORT_CSV: "Export CSV",
    BULK_ASSIGN_LEADS: "Attribution en masse",
    ROUND_ROBIN_ASSIGN: "Distribution automatique",
    CREATE_USER: "Utilisateur créé",
    UPDATE_USER: "Utilisateur modifié",
    DELETE_USER: "Utilisateur supprimé",
    CREATE_STATUS: "Statut créé",
    UPDATE_STATUS: "Statut modifié",
    DELETE_STATUS: "Statut supprimé",
  };
  return labels[actionType] || actionType;
};

export const AuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const limit = 30;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (actionFilter !== "all") params.actionType = actionFilter;
      if (entityFilter !== "all") params.entity = entityFilter;

      const response = await getAuditLogs(params);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, entityFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Historique d'audit
          </h1>
          <p className="text-muted-foreground mt-1">
            Traçabilité complète des actions sur la plateforme
          </p>
        </div>
        <Button variant="outline" onClick={fetchLogs}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[200px]" data-testid="action-filter">
                <SelectValue placeholder="Type d'action" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[180px]" data-testid="entity-filter">
                <SelectValue placeholder="Entité" />
              </SelectTrigger>
              <SelectContent>
                {ENTITIES.map((entity) => (
                  <SelectItem key={entity.value} value={entity.value}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Journal des actions</CardTitle>
          <CardDescription>
            Historique détaillé de toutes les opérations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun log trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/30 transition-colors"
                  data-testid={`audit-log-${log.id}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(
                      log.actionType
                    )}`}
                  >
                    {getActionIcon(log.actionType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {getActionLabel(log.actionType)}
                      </span>
                      <Badge variant="outline">{log.entity}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      par <span className="font-medium">{log.performedByName}</span>
                    </p>
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground font-mono bg-muted/50 rounded p-2">
                        {Object.entries(log.metadata).map(([key, value]) => (
                          <div key={key}>
                            {key}: {typeof value === "object" ? JSON.stringify(value) : String(value)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateTime(log.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Page {page}</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={logs.length < limit}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
